import { yupResolver } from '@hookform/resolvers/yup';
import type { DirtyFields } from '@src/changeRequest/DirtyFields';
import { filterDirtyFieldWithChanges } from '@src/changeRequest/DirtyFields';
import type { Client } from '@src/clients/domain/Client';
import { PageContent } from '@src/common/components/PageContent';
import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import type { FormFields } from '@src/orders/domain/CheckedFields';
import { hasCategory } from '@src/orders/domain/CheckedFields';
import type { Order } from '@src/orders/domain/Order';
import type { PermitInstruction } from '@src/orders/domain/PermitInstruction';
import type { TabKey } from '@src/orders/EditOrderTabs';
import { EditOrderTabs } from '@src/orders/EditOrderTabs';
import { ConditionalFieldsContext } from '@src/orders/PermitFormSections/ConditionalFieldsContext';
import { PermitFormErrorsContextProvider } from '@src/orders/PermitFormSections/PermitFormErrorsContextProvider';
import type { EditOrderFormInputs } from '@src/orders/schemas/EditOrderFormInputs';
import { editOrderFormInputs } from '@src/orders/schemas/EditOrderFormInputs';
import { OrderEntriesComparator } from '@src/orders/services/OrderEntriesComparator';
import { TabPanel } from '@src/orders/TabPanel';
import { tabsPanels } from '@src/orders/TabsPanels';
import { useOrderUnsavedChanges } from '@src/orders/unsavedChanges/useOrderUnsavedChanges';
import { OrdersNavigation } from '@src/routing/OrdersNavigation';
import type { Tractor } from '@src/tractor/domain/Tractor';
import type { TrailerListing } from '@src/trailer/domain/TrailerList';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export interface EditOrderProps {
  order: Order;
  client: Client;
  trailers: TrailerListing[];
  tractors: Tractor[];
  checkedFields: FormFields;
  permitInstructions: PermitInstruction[];
  defaultValues: EditOrderFormInputs;
  onValuesChange: (values: EditOrderFormInputs) => void;
  onSubmit: (formValues: Record<string, unknown>) => void;
  ActionsFooter: (props: {
    order: Order;
    onSubmit: () => void;
    triggerValidation: () => Promise<boolean>;
    formIsDirty: boolean;
    readFormValues: () => Record<string, unknown>;
    readFormDirtyFields: () => DirtyFields;
  }) => JSX.Element;
  disabledFields?: string[];
  canAddPermits?: boolean;
}

export const EditOrder = ({
  order,
  client,
  checkedFields,
  permitInstructions,
  defaultValues,
  onValuesChange,
  trailers,
  tractors,
  ActionsFooter,
  onSubmit,
  disabledFields,
  canAddPermits = true,
}: EditOrderProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { unsavedChanges } = useOrderUnsavedChanges({
    order,
    trailers,
    tractors,
  });
  const restoredValues = unsavedChanges ?? defaultValues;

  const methods = useForm({
    shouldUnregister: false,
    resolver: yupResolver(
      editOrderFormInputs(order, checkedFields.fields, client),
    ),
    defaultValues: restoredValues,
    disabled: false,
    context: {
      mode: 'completeApplication',
    },
  });
  const formValues = methods.watch();
  const orderValues = order.getMostRecentEntries(client) as EditOrderFormInputs;

  const formHasUnsavedChanges = OrderEntriesComparator.hasChanged({
    previousValues: orderValues,
    values: formValues,
  });
  const formValuesHaveChanged = OrderEntriesComparator.hasChanged({
    previousValues: restoredValues,
    values: formValues,
  });

  const readFormValues = () => methods.getValues();
  const readFormDirtyFields = () => {
    const dirtyFields = methods.formState.dirtyFields;
    const currentValues = methods.getValues();
    return filterDirtyFieldWithChanges(dirtyFields, currentValues, orderValues);
  };

  useEffect(() => {
    if (formValuesHaveChanged) {
      onValuesChange(formValues);
    }
  }, [formValuesHaveChanged, formValues, onValuesChange]);

  const [currentTab, setCurrentTab] = useState(0);

  const availableTabs = tabsPanels.filter((tabPanel) =>
    hasCategory(tabPanel.category, checkedFields.fields, permitInstructions),
  );

  const onChangeSection = (tabKey: TabKey) => {
    if (tabKey === 'permits') {
      return navigate(OrdersNavigation.addPermits.url(order.id), {
        state: {
          preservedValues: formValues,
        },
      });
    }
    const tabIndex = availableTabs.findIndex(
      (panel) => panel.category === tabKey,
    );
    setCurrentTab(tabIndex);
  };

  const formRef = useRef<HTMLFormElement | null>(null);
  const triggerFormSubmit = useCallback(() => {
    if (formRef.current) {
      formRef.current.requestSubmit();
    }
  }, [formRef]);
  const readonly = false;

  const contextValue = useMemo(
    () => ({
      disabledFields: disabledFields ?? [],
    }),
    [disabledFields],
  );

  return (
    <FormProvider {...methods}>
      <ConditionalFieldsContext.Provider value={contextValue}>
        <PermitFormErrorsContextProvider>
          <Stack direction="row" gap="16px">
            <Stack
              direction="column"
              style={{
                minWidth: 'fit-content',
                padding: '24px',
                paddingTop: '40px',
                alignItems: 'flex-start',
                backgroundColor: theme.palette.surface.surface,
              }}
            >
              <Typography className={titlePadding} component="h4" variant="h5">
                {t('orderTitle')}
              </Typography>
              <Typography className={titlePadding} variant="subtitle2">
                {order.displayId}
              </Typography>
              <EditOrderTabs
                canAddPermits={canAddPermits}
                checkedFields={checkedFields.fields}
                permitInstructions={permitInstructions}
                currentTab={availableTabs[currentTab]?.category}
                onChangeSection={onChangeSection}
              />
            </Stack>

            <PageContent>
              <form
                onSubmit={methods.handleSubmit(onSubmit)}
                ref={formRef}
                style={{ height: '100%' }}
              >
                <Stack
                  direction="column"
                  justifyContent="flex-start"
                  height="100%"
                  alignItems="stretch"
                  gap={theme.spacing['3']}
                >
                  {tabsPanels
                    .filter((tabPanel) =>
                      hasCategory(
                        tabPanel.category,
                        checkedFields.fields,
                        permitInstructions,
                      ),
                    )
                    .map((tabPanel, index) => (
                      <TabPanel key={index} index={index} value={currentTab}>
                        {tabPanel.render(
                          order,
                          permitInstructions,
                          checkedFields.fields,
                          client,
                          readonly,
                          methods,
                          t,
                        )}
                      </TabPanel>
                    ))}

                  <ActionsFooter
                    order={order}
                    triggerValidation={() => methods.trigger()}
                    onSubmit={triggerFormSubmit}
                    formIsDirty={formHasUnsavedChanges}
                    readFormValues={readFormValues}
                    readFormDirtyFields={readFormDirtyFields}
                  />
                </Stack>
              </form>
            </PageContent>
          </Stack>
        </PermitFormErrorsContextProvider>
      </ConditionalFieldsContext.Provider>
    </FormProvider>
  );
};

const titlePadding = css({
  paddingLeft: '12px',
  fontWeight: 500,
});
